<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  formSubmitted: Boolean,
  sharedData: Object,
  leadGenFields: Object,
  brochureRequestStore: Object,
  buttonText: String,
  btnSubmitClass: String,
  onChanged: Function,
  handleSubmit: Function,
  forceMobileMode: Boolean,
  emailField: Boolean,
  phoneConfig: {
    type: Object,
    default: () => ({ isVisible: false, isMandatory: false })
  },
  mandatoryError: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['onFieldChanged', 'handleSubmit']);

const localFirstNameField = ref(null)
const localEmailField = ref(null)
const localPhoneField = ref(null)

const fieldColClass = computed(() => {
  if (props.forceMobileMode) {
    return 'col-12 mb-3'
  }
  // When phone is visible: Name | Email | Phone | CTA must fit in 12 columns
  return props.phoneConfig?.isVisible
    ? 'col-12 col-lg-3 mb-lg-0 mb-3'
    : 'col-12 col-lg-4 mb-lg-0 mb-3'
})

const buttonColClass = computed(() => {
  // Keep CTA the same width as before (col-lg-2 on desktop)
  return props.forceMobileMode
    ? 'col-12 d-flex justify-content-center mb-3'
    : 'col-12 col-lg-2 d-flex justify-content-center justify-content-lg-center'
})

const phonePlaceholderText = computed(() => {
  const base = (props.leadGenFields?.phonePlaceholderText || 'Phone').replace(/\s*\*$/, '')
  return props.phoneConfig?.isMandatory ? `${base}*` : base
})

function validateFields() {
  localFirstNameField.value?.runValidation?.()
  localEmailField.value?.runValidation?.()
  if (props.phoneConfig?.isVisible) {
    localPhoneField.value?.runValidation?.()
  }
}

defineExpose({ validateFields })

watch(() => props.emailField, (val) => {
  if (val && localEmailField.value) {
    localEmailField.value.handleState('invalid');
  }
});
</script>
<template>
  <div class="block full-width-ribbon" data-load="eager">
    <div class="container">
      <div class="row justify-content-center" v-if="!formSubmitted">
        <div class="col-12 px-5" v-html="sharedData.formHeading"></div>
        <div class="col-lg-10 col-xs-12">
          <form class="needs-validation " ref="formFields" @submit.prevent="emit('handleSubmit', $event)" novalidate id="lead-full-width-ribbon">
            <fieldset disabled="disabled" class="row justify-content-center align-items-center">
              <div :class="fieldColClass">
                <FormFloatingLabelInput :id="'firstName'" :placeholderText="leadGenFields.namePlaceholderText" :errorText="leadGenFields.nameErrorMessage" :required="true" @onChanged="emit('onFieldChanged', $event)" ref="localFirstNameField" />
              </div>
              <div :class="fieldColClass">
                <FormFloatingLabelInput :id="'emailAddress'" :type="'email'" :placeholderText="leadGenFields.emailFields.emailPlaceholderText" :errorText="leadGenFields.emailFields.emailErrorMessage" :invalidText="leadGenFields.emailFields.invalidEmailErrorMessage" :required="true" @onChanged="emit('onFieldChanged', $event)"  ref="localEmailField" />
              </div>
              <div v-if="phoneConfig.isVisible" :class="fieldColClass">
                <FormFloatingLabelInput
                  id="phoneNumber"
                  type="tel"
                  :placeholderText="phonePlaceholderText"
                  :errorText="leadGenFields.phoneErrorText"
                  :required="phoneConfig.isMandatory"
                  @onChanged="emit('onFieldChanged', $event)"
                  ref="localPhoneField"
                />
              </div>
              <div :class="buttonColClass">
                <button class="btn ribbon-submit" :class="[btnSubmitClass, sharedData.primaryCTA.buttonType, sharedData.primaryCTA.buttonAnatomy]" type="submit">
                  {{ buttonText }}
                </button>
              </div>
              <div v-if="mandatoryError" class="form-error-message text-center col-12 mt-2">
                {{ mandatoryError }}
              </div>
              <div v-if="phoneConfig.isVisible" class="form-disclaimer text-center col-12">
                By submitting your number, you consent to receive occasional text messages
                from iD Tech for the purpose of providing more information about our services.
                Texting and data rates may apply.
              </div>
              <div class="form-disclaimer text-center col-12">
                By signing up you agree to our <a href="#privacy-policy" data-bs-toggle="modal" data-bs-target="#privacy-policy-footer">Privacy Policy</a>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
      <div class="row justify-content-center" v-else>
        <div class="col-12 px-5 social-share-container" v-if="brochureRequestStore.isUser && brochureRequestStore.isOptedOutEmail" v-html="sharedData.leadGenOptedOut">
        </div>
        <div class="col-12 social-share-container">
          <UISocialShare :title="''" :division="''" v-if="sharedData.defaultMessage"/>
          <GenericContent :data="{content : sharedData.customLeadGenMessage}" v-else></GenericContent>
        </div>
      </div>
    </div>
  </div> 
</template>


<style lang="scss">
  @include media-breakpoint-up(lg) {
  .full-width-ribbon .ribbon-submit.btn.block {
    width: auto;
  }
  .full-width-ribbon .ribbon-submit {
    width: 100%;
    max-width: none;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .has-floating-footer .full-width-ribbon .ribbon-submit {
    width: auto!important;
  }
  .has-floating-footer .bg-light-grey.section-component {
    border-radius: 24px;
  }
}
.full-width-ribbon .form-error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-bottom: 0!important;
}
</style>
